/**
 * @typedef {{ id: number, code: string, displayName: string }} CompanySource
 */

/**
 * @param {string | null | undefined} value
 * @returns {string | null}
 */
function normalizeCompanySourceDisplayName(value) {
    if (value == null) {
        return null;
    }

    const trimmed = String(value).trim();
    return trimmed === '' ? null : trimmed;
}

/**
 * @param {object[]} maps
 * @returns {Map<number, string>}
 */
function buildDisplayNamesBySourceId(maps) {
    /** @type {Map<number, string>} */
    const byId = new Map();

    for (const map of maps ?? []) {
        if (map.companySourceId != null) {
            const numericId = Number(map.companySourceId);
            const displayName = normalizeCompanySourceDisplayName(map.companySourceDisplayName);

            if (Number.isFinite(numericId) && displayName && !byId.has(numericId)) {
                byId.set(numericId, displayName);
            }
        }
    }

    return byId;
}

/**
 * @param {number | string | null | undefined} companySourceId
 * @param {CompanySource[]} companySources
 * @param {string | null | undefined} [companySourceDisplayName]
 * @returns {CompanySource | null}
 */
function resolveCompanySource(companySourceId, companySources, companySourceDisplayName = null) {
    if (companySourceId == null) {
        return null;
    }

    const numericId = Number(companySourceId);
    const apiDisplayName = normalizeCompanySourceDisplayName(companySourceDisplayName);
    const match = companySources.find((source) => source.id === numericId);

    if (apiDisplayName) {
        return {
            id: numericId,
            code: match?.code ?? `source_${numericId}`,
            displayName: apiDisplayName,
        };
    }

    if (match) {
        return match;
    }

    return {
        id: numericId,
        code: `source_${numericId}`,
        displayName: `Source ${numericId}`,
    };
}

/**
 * @param {object[]} maps
 * @param {CompanySource[]} companySources
 * @param {string} [quantityField='expectedCaseQuantity']
 * @returns {Array<{ companySource: CompanySource, numCases: number }>}
 */
function buildSuggestedAllocations(maps, companySources, quantityField = 'expectedCaseQuantity') {
    if (!maps?.length) {
        return [];
    }

    const displayNamesBySourceId = buildDisplayNamesBySourceId(maps);

    /** @type {Map<number, number>} */
    const casesBySourceId = new Map();

    for (const map of maps) {
        const { companySourceId } = map;
        if (companySourceId != null) {
            const numericId = Number(companySourceId);
            const quantity = Number(map[quantityField]) || 0;
            casesBySourceId.set(numericId, (casesBySourceId.get(numericId) ?? 0) + quantity);
        }
    }

    return [...casesBySourceId.entries()]
        .map(([sourceId, numCases]) => {
            const companySource = resolveCompanySource(sourceId, companySources, displayNamesBySourceId.get(sourceId));
            if (!companySource) {
                return null;
            }
            return { companySource, numCases };
        })
        .filter(Boolean)
        .sort((a, b) => a.companySource.displayName.localeCompare(b.companySource.displayName));
}

/**
 * @param {object[]} maps
 * @param {CompanySource[]} companySources
 * @returns {CompanySource[]}
 */
function buildCompanySourceOptionsFromMaps(maps, companySources) {
    if (!maps?.length) {
        return [];
    }

    const displayNamesBySourceId = buildDisplayNamesBySourceId(maps);
    const sourceIds = new Set(
        maps
            .map((map) => map.companySourceId)
            .filter((id) => id != null)
            .map((id) => Number(id)),
    );

    const globalOptions = companySources.filter((source) => sourceIds.has(source.id));
    const globalIds = new Set(globalOptions.map((source) => source.id));

    const mapOnlyOptions = [...sourceIds]
        .filter((sourceId) => !globalIds.has(sourceId))
        .map((sourceId) => resolveCompanySource(sourceId, companySources, displayNamesBySourceId.get(sourceId)))
        .filter(Boolean);

    return [...globalOptions, ...mapOnlyOptions];
}

export { buildCompanySourceOptionsFromMaps, buildSuggestedAllocations, resolveCompanySource };
